/**
 * All player input of the game screen: touch / pen / mouse anywhere on the play area, the
 * keyboard and gamepads, turned into the timestamped jump timeline (JumpInput) plus a few
 * commands (boost slots, menu, mute, switching the watched racer).
 *
 * - Pointer events cover touch, pen and mouse; every finger is its own source, so holding
 *   with one finger and tapping a boost button with another works. HUD controls (anything
 *   marked with CONTROL_ATTR) never count as jump presses.
 * - Gamepads (pads.ts) have no events: the game loop reads them every frame right before the
 *   simulation steps (pollPads), and each button of each pad is a source of its own
 *   (`pad:<pad>:<button>`), stamped with the pad's own time of the change. While the in-game
 *   menu is open the D-pad and the stick move its focus and A presses the focused button;
 *   while a dialog of the shell covers the game, the pads are the shell's (app/padNavigation).
 * - The play area blocks scrolling, zooming, long-press menus and text selection.
 * - pointercancel, losing focus and hiding the page release everything, so the cube never
 *   keeps jumping on its own after an interruption; a button still held after that counts
 *   again only when pressed again (keys and pads alike).
 */
import { eventTime } from './eventTime';
import { moveFocus, pressFocused } from './focusNav';
import { type GamepadHub, gamepads, type PadConsumer, type PadRumble } from './gamepads';
import { JumpInput } from './jumpInput';
import { behindShellDialog, ignoreKeyEvent, keyCommand } from './keys';
import type { PadEdge } from './padReader';
import { PAD, type PadCommand, padCommands, padDirection } from './pads';

/** Elements with this attribute (and their children) are HUD controls, not the play area. */
export const CONTROL_ATTR = 'data-game-control';

export interface GameInputHandlers {
  /** Boost slot 0 or 1 from the keyboard or a pad (the HUD buttons call the game directly). */
  onBoost(slot: number): void;
  onMenu(): void;
  /** A pad's B: closes the menu if it is open (nothing else in a race). */
  onBack(): void;
  onMute(): void;
  /** Spectators: watch the previous / next racer. */
  onSwitch(step: -1 | 1): void;
  /** A press on the play area itself, jumping or not (spectators switch racers with it). */
  onAreaPress(): void;
}

export class GameInput {
  readonly jump = new JumpInput();
  private jumpEnabled = false;
  private readonly detach: Array<() => void> = [];
  private readonly padConsumer: PadConsumer = { onPad: (edge) => this.onPad(edge) };

  constructor(
    private readonly area: HTMLElement,
    private readonly handlers: GameInputHandlers,
    private readonly clock: () => number,
    private readonly pads: GamepadHub = gamepads,
  ) {
    this.listen(area, 'pointerdown', (e) => this.onPointerDown(e as PointerEvent), false);
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      this.listen(area, type, (e) => this.onPointerUp(e as PointerEvent));
    }
    // A mouse released outside the window never reaches the area.
    for (const type of ['pointerup', 'pointercancel'] as const) {
      this.listen(window, type, (e) => this.onPointerUp(e as PointerEvent));
    }
    // iOS: no double-tap zoom, magnifier, callout or scroll bounce from the play area. This
    // also suppresses the emulated mouse events and clicks, which the play area never needs.
    this.listen(area, 'touchstart', (e) => this.blockDefault(e), false);
    this.listen(area, 'contextmenu', (e) => this.blockDefault(e), false);
    this.listen(area, 'selectstart', (e) => this.blockDefault(e), false);
    this.listen(area, 'dragstart', (e) => this.blockDefault(e), false);
    this.listen(area, 'gesturestart', (e) => e.preventDefault(), false);

    this.listen(window, 'keydown', (e) => this.onKeyDown(e as KeyboardEvent), false);
    this.listen(window, 'keyup', (e) => this.onKeyUp(e as KeyboardEvent), false);
    this.listen(window, 'blur', () => this.releaseAll());
    this.listen(window, 'pagehide', () => this.releaseAll());
    this.listen(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.releaseAll();
    });
    // The game reads the pads itself (pollPads) and has them first.
    this.detach.push(pads.listen(this.padConsumer), pads.drive());
  }

  /** Whether presses count as jumps (off before the start, for spectators, behind menus). */
  setJumpEnabled(enabled: boolean): void {
    if (this.jumpEnabled === enabled) return;
    this.jumpEnabled = enabled;
    if (!enabled) this.releaseAll();
  }

  releaseAll(): void {
    this.jump.releaseAll(this.clock());
  }

  /**
   * Reads the gamepads (at `now`, the engine clock): the game loop calls this every frame,
   * right before the simulation steps, so a press lands on the tick it happened in.
   */
  pollPads(now: number): void {
    this.pads.poll(now);
  }

  /** A short rumble of the pad played with (a crash), where there is one. */
  rumble(effect: PadRumble): void {
    this.pads.rumble(effect);
  }

  dispose(): void {
    for (const off of this.detach.splice(0)) off();
    this.jump.discard();
  }

  private onPointerDown(event: PointerEvent): void {
    if (isControl(event.target)) return;
    // Only the main mouse button; touch and pen report button 0 too.
    if (event.button > 0) return;
    event.preventDefault();
    this.handlers.onAreaPress();
    if (!this.jumpEnabled) return;
    try {
      // Up / cancel then always come back here, even from outside the area.
      this.area.setPointerCapture(event.pointerId);
    } catch {
      // Not capturable (synthetic or already released pointer): window listeners cover it.
    }
    this.jump.press(`pointer:${event.pointerId}`, eventTime(event, this.clock()));
  }

  private onPointerUp(event: PointerEvent): void {
    // Letting go of another mouse button does not end a hold of the main one.
    if (event.type === 'pointerup' && event.button > 0) return;
    this.jump.release(`pointer:${event.pointerId}`, eventTime(event, this.clock()));
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (ignoreKeyEvent(event, this.area)) return;
    const command = keyCommand(event.code, event.key);
    if (!command) return;
    switch (command.kind) {
      case 'jump':
        // Behind the menu, Space belongs to the focused menu button.
        if (!this.jumpEnabled) return;
        event.preventDefault();
        if (!event.repeat) this.jump.press(`key:${event.code}`, eventTime(event, this.clock()));
        return;
      case 'boost':
        event.preventDefault();
        if (!event.repeat) this.handlers.onBoost(command.slot);
        return;
      case 'menu':
        if (!event.repeat) this.handlers.onMenu();
        return;
      case 'mute':
        event.preventDefault();
        if (!event.repeat) this.handlers.onMute();
        return;
      case 'switch':
        event.preventDefault();
        if (!event.repeat) this.handlers.onSwitch(command.step);
        return;
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    // Releases always count: the press may have started before a dialog opened.
    const command = keyCommand(event.code, event.key);
    if (command?.kind !== 'jump') return;
    if (this.jump.held) event.preventDefault();
    this.jump.release(`key:${event.code}`, eventTime(event, this.clock()));
  }

  private onPad(edge: PadEdge): boolean {
    const source = `pad:${edge.pad}:${edge.button}`;
    if (!edge.down) {
      // Releases always count: the press may have come before a dialog opened.
      this.jump.release(source, edge.time);
      return false;
    }
    // A dialog of the shell over the game (settings): its buttons, the shell's pads.
    if (behindShellDialog(this.area, document.activeElement)) return false;
    // The in-game menu: the D-pad and the stick move its focus, A presses the focused button.
    const menu = this.area.querySelector('[aria-modal="true"]');
    if (menu) {
      const direction = padDirection(edge.button);
      if (direction) {
        moveFocus(menu, direction);
        return true;
      }
      if (!edge.repeat && edge.button === PAD.A) {
        pressFocused(menu);
        return true;
      }
    }
    // Held directions repeat for menus only: in a race a press is a press.
    if (edge.repeat) return true;
    for (const command of padCommands(edge.button)) this.onPadCommand(command, source, edge.time);
    return true;
  }

  private onPadCommand(command: PadCommand, source: string, time: number): void {
    switch (command.kind) {
      case 'jump':
        if (this.jumpEnabled) this.jump.press(source, time);
        return;
      case 'boost':
        this.handlers.onBoost(command.slot);
        return;
      case 'menu':
        this.handlers.onMenu();
        return;
      case 'back':
        this.handlers.onBack();
        return;
      case 'mute':
        this.handlers.onMute();
        return;
      case 'switch':
        this.handlers.onSwitch(command.step);
        return;
    }
  }

  private blockDefault(event: Event): void {
    if (!isControl(event.target)) event.preventDefault();
  }

  private listen(
    target: EventTarget,
    type: string,
    handler: (event: Event) => void,
    passive = true,
  ): void {
    target.addEventListener(type, handler, { passive });
    this.detach.push(() => target.removeEventListener(type, handler));
  }
}

/** True if `target` is (inside) a HUD control: buttons there never jump. */
export function isControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${CONTROL_ATTR}]`) !== null;
}
